// The admin sidebar is stored in the DB (seeded from the backend's
// `DEFAULT_SIDEBAR`). Rows carry English `label`s, but every seeded row has a
// stable `name`. Map those names to dictionary keys so the menu and breadcrumbs
// localize with the active language. Operator-added rows that aren't in the map
// fall back to their stored label, so nothing disappears.
export const SIDEBAR_LABEL_KEYS: Record<string, string> = {
  dashboard: "nav.dashboard",
  foundation: "nav.foundation",
  "foundation.tenants": "nav.tenants",
  "cms.config": "nav.brandConfig",
  base: "nav.base",
  "base.languages": "nav.languages",
  "base.currencies": "nav.currencies",
  "base.countries": "nav.countries",
  "base.translations": "nav.translations",
  users: "nav.users",
  "users.roles": "nav.roles",
  "users.agents": "nav.agents",
  "users.players": "nav.players",
  security: "nav.security",
  "security.sessions": "nav.sessions",
  "security.auditLog": "nav.auditLog",
  "security.backups": "nav.backups",
  "security.loginHistory": "nav.loginHistory",
  "security.trackingWhitelist": "nav.trackingWhitelist",
  "security.geoAccess": "nav.geoAccess",
  "security.serverSwitches": "nav.serverSwitches",
  compliance: "nav.compliance",
  "compliance.kyc": "kyc.title",
  "compliance.risk": "nav.riskFlags",
  "compliance.betLimits": "nav.betLimits",
  "compliance.chat": "nav.chatModeration",
  payment: "nav.payment",
  "payment.providers": "nav.paymentProviders",
  "payment.withdrawals": "withdrawals.title",
  "payment.method_rules": "nav.paymentMethodRules",
  "payment.balance_adjustments": "nav.balanceAdjustments",
  "payment.sky_settings": "nav.skySettings",
  "payment.swap_approvals": "nav.swapApprovals",
  game: "nav.game",
  "game.aggregator": "aggregator.title",
  "game.gameType": "nav.gameType",
  "game.category": "nav.gameCategory",
  "game.provider": "nav.provider",
  "game.bots": "nav.gameBots",
  "game.games": "nav.games",
  promotion: "nav.promotion",
  "promotion.vipList": "nav.vipList",
  "promotion.vipSources": "nav.vipSources",
  "promotion.template": "nav.campaignTemplate",
  "promotion.dailySpin": "nav.dailySpin",
  "promotion.jackpot": "nav.jackpots",
  "promotion.wagerRace": "nav.wagerRace",
  "promotion.challenge": "nav.challenges",
  "promotion.raffle": "nav.raffles",
  "promotion.rakeback": "nav.rakeback",
  "promotion.vipBoost": "nav.vipBoost",
  "promotion.vipHost": "nav.vipHosts",
  "promotion.dailyCheckin": "nav.dailyCheckin",
  "promotion.mission": "nav.missions",
  "promotion.loyaltyStore": "nav.loyaltyStore",
  template: "nav.template",
  "template.templates": "nav.templates",
  "template.sidebar": "nav.cmsSidebar",
  "template.banner": "nav.banner",
  "template.faq": "nav.cmsFaq",
  "template.footer": "nav.footerManage",
  "template.footer.list": "nav.footerList",
  "template.footer.item": "nav.footerItem",
  "template.footer.image": "nav.footerImage",
  cms: "nav.cms",
  "cms.sidebar": "nav.brandSidebar",
  "cms.banner": "nav.brandBanner",
  "cms.footer": "nav.brandFooter",
  "cms.footer.list": "nav.footerList",
  "cms.footer.item": "nav.footerItem",
  "cms.footer.image": "nav.footerImage",
  crm: "nav.crm",
  "crm.notification": "nav.notification",
  "crm.supportHistory": "nav.supportHistory",
  "crm.helpCategories": "nav.helpCategories",
  "crm.helpArticles": "nav.helpArticles",
  "crm.sitePages": "nav.sitePages",
  "crm.supportWidget": "nav.supportWidget",
  report: "nav.report",
  "report.analytics": "nav.analytics",
  "report.schedules": "nav.reportSchedules",
  "report.solvency": "nav.solvency",
  "report.game": "nav.gameReport",
  "report.payment": "nav.paymentReport",
  "report.wallet_ledger": "nav.walletLedger",
  "report.bonus_grant": "nav.bonusGrantHistory",
  "report.bonus_wallet_ledger": "nav.bonusBalanceHistory",
  "report.daily_spin": "nav.dailySpinHistory",
  "report.wager_race": "nav.wagerRaceResults",
  "report.slot_battle": "nav.slotBattles",
  "report.raffle": "nav.raffleResults",
  "report.rakeback": "nav.rakebackClaims",
  "report.vip_boost": "nav.vipBoostRewards",
  "report.daily_checkin": "nav.dailyCheckins",
  "report.mission": "nav.missionProgress",
  "report.loyalty_store": "nav.loyaltyStorePurchases",
  "report.tipping": "nav.tipping",
  permission: "nav.permission",
  "permission.sidebar": "nav.sidebarManage",
  "permission.groups": "nav.groupManage",
  "permission.functions": "nav.functionsManage",
  "permission.icons": "icons.title",
};

// Localize a sidebar row via its stable `name`; fall back to the stored English
// label for operator-added rows that aren't in the map.
export const resolveSidebarLabel = (
  name: string,
  label: string,
  t: (key: string) => string,
): string => {
  const key = SIDEBAR_LABEL_KEYS[name];

  return key ? t(key) : label;
};
